import { Box, Button, Typography, IconButton, Divider } from '@mui/material'
import { useTranslator } from '@/hooks/useTranslator'
import dayjs from 'dayjs'
import { EmployerAddNote } from './EmployerAddNote'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Company, IHttpError, useCompanyDeleteNoteContractMutation } from '@/@data'
import { useState } from 'react'
import { useNotification } from '@/hooks/useNotification'

export const EmployerNote = () => {
  const { __ } = useTranslator()
  const [open, setOpen] = useState(false)
  const { id } = useParams()
  const n = useNotification()

  const { data: dataNote, refetch } = useQuery({
    ...Company.getNoteContract(Number(id)),
    enabled: !!id
  })

  const { mutate: deleteNoteContract } = useCompanyDeleteNoteContractMutation()

  const groupedNotes =
    dataNote?.data?.data?.reduce(
      (groups: Record<string, any[]>, note: any) => {
        const date = dayjs(note.created_at).format('MMMM D, YYYY')

        if (!groups[date]) {
          groups[date] = []
        }

        groups[date].push(note)

        return groups
      },
      {} as Record<string, any[]>
    ) || {}

  const handleDeleteNote = (noteId: number) => {
    deleteNoteContract(
      { companyId: Number(id), noteId },
      {
        onSuccess: () => {
          n.success(__('Delete note successfully'))
          refetch()
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  return (
    <Box className='flex flex-col gap-6 p-6'>
      <div className='flex flex-col gap-4'>
        <div className='flex justify-between'>
          <Typography className='text-[18px] leading-[28px] font-medium text-[#2f2b3de6]'>{__('Note')}</Typography>
          <Button
            variant='contained'
            className='text-[#3551F2] bg-[#E8E6FC] shadow-none rounded-full'
            startIcon={<i className='tabler-note' />}
            onClick={() => setOpen(true)}
          >
            <span className='text-[13px] font-medium'>{__('Add Note')}</span>
          </Button>
        </div>
        <Typography className='text-[15px] font-normal '>
          {__('This is where you can add and manage all your notes for this Employer.')}
        </Typography>
      </div>

      <Box className=' bg-[#F3F2F3] border rounded-[16px]'>
        {Object.keys(groupedNotes).length > 0 ? (
          Object.entries(groupedNotes).map(([date, dateNotes], dateIndex) => {
            const totalDates = Object.keys(groupedNotes).length

            return (
              <Box key={date} className='last:mb-0'>
                <Typography className='text-[15px] leading-[22px] font-medium text-[#2F2B3D] p-4'>{date}</Typography>

                {(dateNotes as any[]).map((note: any, index: number) => (
                  <Box
                    key={note.id}
                    className={`bg-[#fff] ${
                      dateIndex === totalDates - 1 && index === (dateNotes as any[]).length - 1
                        ? 'rounded-b-[16px]'
                        : ''
                    }`}
                  >
                    <Box className='flex justify-between items-start gap-4 p-3'>
                      <Box className='flex items-center gap-2 w-[40%]'>
                        <Typography className='text-[14px] leading-[20px] font-normal text-[#2f2b3db3]'>
                          {dayjs(note.created_at).format('D MMM YYYY, h:mm A')}
                        </Typography>
                        <Typography className='text-[14px] leading-[20px] font-medium text-[#2f2b3db3]'>
                          {note.created_user?.first_name || 'Unknown User'} - {note.created_user?.employee_id || 'N/A'}
                        </Typography>
                      </Box>

                      <Box className='flex items-center gap-2 w-[60%] justify-end'>
                        <Typography className='text-[15px] leading-[20px] font-normal text-[#2f2b3db3]'>
                          {note.note}
                        </Typography>
                        <IconButton
                          size='small'
                          sx={{
                            color: '#2F2B3D',
                            '&:hover': {
                              backgroundColor: 'rgba(47, 43, 61, 0.04)'
                            }
                          }}
                          onClick={() => handleDeleteNote(note.id)}
                        >
                          <i className='tabler-trash size-[16px]' />
                        </IconButton>
                      </Box>
                    </Box>

                    {index < (dateNotes as any[]).length - 1 && <Divider sx={{ borderColor: '#E0E0E0' }} />}
                  </Box>
                ))}
              </Box>
            )
          })
        ) : (
          <Box className='p-6 text-center h-[200px] flex items-center justify-center'>
            <Typography className='text-[15px] leading-[22px] font-normal text-[#2F2B3D99]'>
              {__('No notes available.')}
            </Typography>
          </Box>
        )}
      </Box>
      <EmployerAddNote open={open} onClose={() => setOpen(false)} refetch={refetch} />
    </Box>
  )
}
